import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MousePointerClick, QrCode, TrendingUp, TrendingDown, Calendar, Globe, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

function weekChange(current, previous) {
  if (!previous) return { pct: current > 0 ? 100 : 0, direction: current > 0 ? 'up' : 'neutral' };
  if (current === previous) return { pct: 0, direction: 'neutral' };
  const pct = Math.round(((current - previous) / previous) * 100);
  return { pct: Math.abs(pct), direction: pct > 0 ? 'up' : 'down' };
}

function ChangeIndicator({ current, previous }) {
  const { pct, direction } = weekChange(current, previous);
  if (direction === 'neutral') {
    return <span className="text-xs text-slate-400 font-body flex items-center gap-0.5"><Minus className="w-3 h-3" />zur Vorwoche</span>;
  }
  if (direction === 'up') {
    return <span className="text-xs text-emerald-600 font-body flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" />+{pct}% zur Vorwoche</span>;
  }
  return <span className="text-xs text-red-500 font-body flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" />-{pct}% zur Vorwoche</span>;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getStatistics().then(res => setStats(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Armaturenbrett</h1>
        <p className="text-sm text-slate-500 font-body mt-1">Uebersicht Ihrer Mietwagen-Landingpage</p>
      </div>

      {/* Kennzahlen mit Wochenvergleich */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm rounded-sm" data-testid="stat-clicks-today">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#0E518D] rounded-sm flex items-center justify-center flex-shrink-0">
              <MousePointerClick className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-body">{stats?.clicks_today || 0}</p>
              <p className="text-xs text-slate-500 font-body">Affiliate-Klicks heute</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-sm" data-testid="stat-clicks-week">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-sm flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-body">{stats?.clicks_week || 0}</p>
              <p className="text-xs text-slate-500 font-body">Klicks diese Woche</p>
              <ChangeIndicator current={stats?.clicks_week || 0} previous={stats?.clicks_prev_week} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-sm" data-testid="stat-clicks-month">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-600 rounded-sm flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-body">{stats?.clicks_month || 0}</p>
              <p className="text-xs text-slate-500 font-body">Klicks diesen Monat</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-sm" data-testid="stat-qr-scans">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-teal-600 rounded-sm flex items-center justify-center flex-shrink-0">
              <QrCode className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-body">{stats?.qr_scans || 0}</p>
              <p className="text-xs text-slate-500 font-body">QR-Code Scans</p>
              <ChangeIndicator current={stats?.qr_scans_week || 0} previous={stats?.qr_scans_prev_week} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-sm" data-testid="stat-total">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-600 rounded-sm flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-body">{stats?.total || 0}</p>
              <p className="text-xs text-slate-500 font-body">Gesamt (alle Quellen)</p>
              <ChangeIndicator current={stats?.week || 0} previous={stats?.prev_week} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Traffic-Quellen und Schnellzugriff */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm rounded-sm" data-testid="traffic-sources">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-body font-semibold text-slate-700">Klicks nach Traffic-Quelle</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.by_traffic_source?.length > 0 ? (
              <div className="space-y-2">
                {stats.by_traffic_source.map((s, i) => {
                  const maxCount = stats.by_traffic_source[0]?.count || 1;
                  const pct = Math.round((s.count / maxCount) * 100);
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-body text-slate-700">{s.traffic_source}</span>
                        <span className="text-sm font-bold font-body text-slate-900">{s.count}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#0E518D] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400 font-body">Noch keine Daten</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-body font-semibold text-slate-700">Schnellzugriff</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { to: '/admin/content', label: 'Inhalte bearbeiten' },
              { to: '/admin/settings', label: 'Affiliate-URL aendern' },
              { to: '/admin/media', label: 'Medien verwalten' },
              { to: '/admin/hero-combinations', label: 'Hero-Kombinationen' },
            ].map((link, i) => (
              <Link key={i} to={link.to} className="block px-3 py-2 rounded-sm hover:bg-slate-50 text-sm font-body text-[#0E518D] transition-colors" data-testid={`quick-link-${i}`}>
                {link.label}
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Letzte Aktivitaeten */}
      <Card className="border-0 shadow-sm rounded-sm" data-testid="recent-activity">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-body font-semibold text-slate-700">Letzte Aktivitaeten</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-body text-xs">Typ</TableHead>
                    <TableHead className="font-body text-xs">Quelle</TableHead>
                    <TableHead className="font-body text-xs">Zeitpunkt</TableHead>
                    <TableHead className="font-body text-xs">Geraet</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.recent.slice(0, 10).map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Badge variant={item.type === 'click' ? 'default' : 'secondary'} className="font-body text-xs">
                          {item.type === 'click' ? 'Klick' : 'QR-Scan'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-body text-slate-600">{item.traffic_source || item.source || '-'}</TableCell>
                      <TableCell className="text-sm font-body text-slate-600">
                        {new Date(item.timestamp).toLocaleString('de-DE')}
                      </TableCell>
                      <TableCell className="text-sm font-body text-slate-600">{item.device || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 font-body py-4 text-center">Noch keine Tracking-Daten</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
